/**
 * PostHog posture for Bonni. Pure: no posthog-js import, no DOM, no env reads,
 * no side effects - so the repo's `node --test` runner can pin it.
 *
 * ── Why this file owns the masking instead of the markup ───────────────────
 *
 * An earlier cut put a `ph-no-capture` class on the chat transcript container
 * in `pages/Chat.tsx`. That worked, and it coupled recording to somebody
 * else's JSX: the intake UI is actively being reworked, and a refactor that
 * moved or rebuilt that container would have silently started recording the
 * conversations - with nothing failing and no test able to notice.
 *
 * Masking by SELECTOR keeps the whole posture in this file. It is blunter
 * (all text is masked, not just the transcript) and that is the right trade:
 * the research question is where people hesitate, scroll and stall, which
 * survives masking, and the failure mode of being too blunt is a less
 * readable replay rather than a leaked conversation.
 */

// ----------------------------------------------------------------------

/**
 * Absent or blank key => analytics is off and posthog-js is never imported.
 * This is what lets the feature ship dark and be switched on with an env var.
 */
export function shouldEnableAnalytics(key: string | undefined | null): boolean {
  return typeof key === "string" && key.trim().length > 0;
}

/**
 * Query parameters whose values PostHog must never store, matched by exact
 * name. `token` is the invite token (/invite) and `inviteToken` the same token
 * on /verify; either one accepts an invitation. `email` prefills /login,
 * /forgot-password and /verify. The Supabase session parameters normally arrive
 * in the fragment, which is dropped whole; they are listed so a flow that moves
 * them into the query string stays covered.
 *
 * `code` is deliberately absent: on /auth/error it is our own reason code
 * (invite-only, cohort refusals), which the study reads.
 */
export const MASKED_URL_PARAMETERS = [
  "token",
  "inviteToken",
  "email",
  "access_token",
  "refresh_token",
  "provider_token",
  "provider_refresh_token",
] as const;

const MASKED_VALUE = "<masked>";

/**
 * Event properties that hold a whole URL. posthog-js masks the current URL as
 * it captures it, but sends `document.referrer` raw, so the page after
 * /invite?token=... would report the token as its referrer. It also replays
 * values a browser saved before masking existed.
 */
const URL_PROPERTIES = [
  "$current_url",
  "$referrer",
  "$initial_current_url",
  "$initial_referrer",
  "$session_entry_url",
  "$session_entry_referrer",
] as const;

/** Drops the fragment and masks MASKED_URL_PARAMETERS in the query string. */
export function maskCapturedUrl(url: string): string {
  const [withoutFragment] = url.split("#", 1);
  const queryStart = withoutFragment.indexOf("?");
  if (queryStart === -1) return withoutFragment;

  const query = withoutFragment
    .slice(queryStart + 1)
    .split("&")
    .map((pair) => {
      const [name] = pair.split("=", 1);
      return (MASKED_URL_PARAMETERS as readonly string[]).includes(name)
        ? `${name}=${MASKED_VALUE}`
        : pair;
    })
    .join("&");

  return `${withoutFragment.slice(0, queryStart)}?${query}`;
}

type PropertyBag = Record<string, unknown>;

function maskUrlProperties(properties: unknown) {
  if (!properties || typeof properties !== "object") return;
  const bag = properties as PropertyBag;
  for (const key of URL_PROPERTIES) {
    const value = bag[key];
    if (typeof value === "string") bag[key] = maskCapturedUrl(value);
  }
}

/**
 * `before_send` hook. Touches only URL_PROPERTIES: every event also carries a
 * `token` property, which is the project key that ingestion requires, and that
 * must survive.
 */
export function maskEventUrls<
  T extends { properties?: PropertyBag; $set?: PropertyBag; $set_once?: PropertyBag },
>(event: T | null): T | null {
  if (!event) return event;
  const { properties, $set, $set_once } = event;
  for (const bag of [properties, $set, $set_once, properties?.$set, properties?.$set_once]) {
    maskUrlProperties(bag);
  }
  return event;
}

/**
 * `session_recording.maskCapturedNetworkRequestFn`. Replay stores each request's
 * URL as its `name`, and the options above don't reach it, so GET
 * /api/invite?token=... would be recorded whole. posthog-js also runs replay page
 * hrefs through this function.
 *
 * Supplying it turns off posthog-js's own scrubbing of request and response
 * bodies, so bodies are dropped here. Replay never needs them, and they would
 * carry the conversations the masking in this file exists to keep out.
 */
export function maskCapturedRequest<
  T extends { name?: string; requestBody?: unknown; responseBody?: unknown },
>(request: T): T {
  const masked = { ...request };
  if (masked.name) masked.name = maskCapturedUrl(masked.name);
  delete masked.requestBody;
  delete masked.responseBody;
  return masked;
}

/**
 * Init options for posthog-js. Returned as a value rather than passed inline
 * at the call site so a test can assert the posture instead of trusting review.
 */
export function buildPosthogOptions(apiHost: string) {
  return {
    api_host: apiHost,

    /**
     * OFF, and this is load-bearing rather than conservative.
     *
     * Autocapture puts the clicked element's TEXT into `$el_text` on the
     * event. `maskTextSelector` governs SESSION REPLAY only - it does not
     * touch event properties. So autocapture would carry the transcript out
     * through a door the mask does not cover, and the recording would look
     * correctly masked while the events were not.
     */
    autocapture: false,

    capture_pageview: true,
    capture_pageleave: true,

    /** No anonymous person profiles. Identity is never email or name. */
    person_profiles: "identified_only" as const,

    /**
     * Supabase's OAuth redirect lands on /auth/post-auth with the session in
     * the fragment (#access_token=...&refresh_token=...), and PostHog was
     * storing that URL whole: as `$current_url` on the pageview, in
     * `$initial_current_url` and `$session_entry_url`, and as the replay's
     * page href. This strips the fragment from all of them. Nothing here
     * routes by hash (BrowserRouter), so the fragment carries nothing worth
     * keeping.
     */
    disable_capture_url_hashes: true,

    /**
     * The same URL fields keep their query string, and so does the replay
     * href. Parameters named in MASKED_URL_PARAMETERS are captured as
     * `<masked>`. This also masks PostHog's own list of ad click ids (gclid,
     * fbclid, ...), which nothing here uses.
     */
    mask_personal_data_properties: true,
    custom_personal_data_properties: [...MASKED_URL_PARAMETERS],

    before_send: maskEventUrls,

    session_recording: {
      maskAllInputs: true,
      /**
       * Everything. Not a class on a container in a file we do not own -
       * see the header. Layout, scrolling, dwell and hesitation all survive.
       */
      maskTextSelector: "*",
      maskCapturedNetworkRequestFn: maskCapturedRequest,
    },
  };
}

// ----------------------------------------------------------------------

export type OrganizationAction = "register" | "reset" | "none";

/**
 * What to do when the organization context changes.
 *
 * The whole point is the `reset` guard. `currentOrganization` is null while the
 * provider is still loading, which is the initial state on EVERY page load - so
 * treating "no organization" as "clear the identity" would call reset() on each
 * load and mint a fresh distinct_id every time.
 *
 * That is not just noisy: it would destroy the distinct_id carried across from
 * router.bizly.com by the cross-subdomain cookie, breaking the one journey the
 * study most wants to watch. Bonfiire's own analytics guards the same way -
 * reset only on a genuine transition away from a known identity.
 */
export function nextOrganizationAction(args: {
  loading: boolean;
  organizationId: string | null | undefined;
  lastOrganizationId: string | null;
}): OrganizationAction {
  const { loading, organizationId, lastOrganizationId } = args;

  if (organizationId) {
    return organizationId === lastOrganizationId ? "none" : "register";
  }

  // Absent organization: only meaningful once loading has finished AND we
  // previously had one. Otherwise it is the initial state, not a sign-out.
  if (loading) return "none";
  return lastOrganizationId === null ? "none" : "reset";
}
